import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Users, Sparkles, ArrowLeft, Check, AlertCircle } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PhotoUploader from "../components/PhotoUploader";
import { INTERESTS, PARTNER_VALUES, MAX_INTERESTS, MIN_INTERESTS, MAX_VALUES, HAS_CHILDREN, MARITAL_STATUS, SMOKING, ALCOHOL, RESIDENCE_COUNTRIES, COUNTRIES, LANGUAGES } from "../lib/profileOptions";
import { track } from "../lib/analytics";

const CardRadio = ({ options, value, onSelect, cols = 2, group }) => (
  <div className={`grid gap-2.5 ${cols === 2 ? "grid-cols-2" : "grid-cols-1"}`} role="radiogroup" data-testid={group}>
    {options.map((o) => {
      const active = value === o.value;
      return (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={active}
          data-testid={`${group}-${o.value}`}
          onClick={() => onSelect(o.value)}
          className={`flex items-center gap-2.5 px-4 py-3.5 rounded-2xl border text-left transition-all active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-1 ${
            active
              ? "bg-green-50 border-green-500 ring-green-300/50 shadow-[0_4px_14px_rgba(34,197,94,0.18)]"
              : "bg-white border-kora-line hover:border-green-500/40 ring-transparent"
          }`}
        >
          <span className="text-xl leading-none" aria-hidden="true">{o.emoji}</span>
          <span className={`text-sm font-semibold ${active ? "text-kora-ink" : "text-kora-ink/80"}`}>{o.label}</span>
        </button>
      );
    })}
  </div>
);

const Section = ({ emoji, title, question, children, error, status }) => (
  <div className={`rounded-3xl border p-4 ${error ? "bg-red-50 border-2 border-red-400" : "bg-white/60 border-kora-line"}`}>
    <div className="flex items-center gap-2 mb-1">
      <span className="text-lg" aria-hidden="true">{emoji}</span>
      <h3 className="font-display font-bold text-kora-ink">{title}</h3>
      {status}
    </div>
    {question && <p className="text-sm text-kora-muted mb-3">{question}</p>}
    {children}
  </div>
);

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [photos, setPhotos] = useState(user?.photos || []);
  const [form, setForm] = useState({
    name: "",
    gender: "",
    interested_in: "",
    age: "",
    city: "",
    residence_country: "",
    languages: "",
    goal: "",
    bio: "",
    body_type: "",
    education: "",
    height: "",
    has_children: "",
    marital_status: "",
    smoking: "",
    alcohol: "",
  });
  const [interests, setInterests] = useState([]);
  const [partnerValues, setPartnerValues] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const fieldValid = {
    photos: photos.length > 0,
    name: form.name.trim().length > 0,
    gender: !!form.gender,
    interested_in: !!form.interested_in,
    goal: !!form.goal,
    age: form.age !== "" && parseInt(form.age) >= 18,
    city: !!form.city,
    residence_country: !!form.residence_country,
    languages: !!form.languages,
    body_type: !!form.body_type,
    height: form.height !== "" && parseInt(form.height) > 0,
    education: !!form.education,
    has_children: !!form.has_children,
    marital_status: !!form.marital_status,
    smoking: !!form.smoking,
    alcohol: !!form.alcohol,
    interests: interests.length >= MIN_INTERESTS,
    partner_values: partnerValues.length > 0,
    bio: form.bio.trim().length >= 30,
  };
  const FIELD_LABELS = {
    photos: "Vos photos (au moins 1)",
    name: "Votre prénom",
    gender: "Je suis (homme / femme)",
    interested_in: "Je recherche (homme / femme)",
    goal: "Quel est votre but ? (amicale / mariage)",
    age: "Âge (18 ans minimum)",
    city: "Pays d'origine",
    residence_country: "Pays de résidence",
    languages: "Langue parlée",
    body_type: "Ma silhouette",
    height: "Taille (cm)",
    education: "Niveau d'étude",
    has_children: "Enfants",
    marital_status: "Situation matrimoniale",
    smoking: "Tabac",
    alcohol: "Alcool",
    interests: `Vos centres d'intérêt (minimum ${MIN_INTERESTS})`,
    partner_values: "Ce que vous recherchez chez un partenaire (au moins 1)",
    bio: "Votre présentation (minimum 30 caractères)",
  };
  const missing = Object.keys(fieldValid).filter((k) => !fieldValid[k]);
  const isErr = (k) => showErrors && !fieldValid[k];
  const inputCls = (k) =>
    `w-full px-4 py-3 rounded-xl border bg-white focus:outline-none ${
      isErr(k) ? "border-red-500 bg-red-50 focus:border-red-500" : fieldValid[k] ? "border-green-500 focus:border-green-600" : "border-kora-line focus:border-kora-terracotta"
    }`;
  const LabelStatus = ({ k }) =>
    fieldValid[k] ? (
      <Check size={15} className="inline text-green-600 ml-1 align-[-2px]" strokeWidth={3} />
    ) : isErr(k) ? (
      <span className="ml-2 text-xs font-bold text-red-500">À compléter</span>
    ) : null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleFrom = (value, list, setList, max) => {
    if (list.includes(value)) {
      setList(list.filter((v) => v !== value));
    } else {
      if (list.length >= max) {
        toast.error(`Maximum ${max} sélections.`);
        return;
      }
      setList([...list, value]);
    }
  };

  const onPhotos = (next) => {
    setPhotos(next);
    setUser((u) => ({ ...u, photos: next, photo_url: next[0] || u?.photo_url }));
  };

  const submit = async () => {
    if (missing.length > 0) {
      setShowErrors(true);
      toast.error("Formulaire incomplet — les champs à compléter sont indiqués en rouge.", { duration: 5000 });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, age: parseInt(form.age) };
      if (form.height) payload.height = parseInt(form.height); else delete payload.height;
      if (photos.length > 0) payload.photo_url = photos[0];
      if (interests.length) payload.interests = interests;
      if (partnerValues.length) payload.partner_values = partnerValues;
      const { data } = await api.put("/profile", payload);
      setUser(data);
      track("sign_up", { method: "google" });
      if (!data.verified) {
        toast.success("Profil créé ! Dernière étape : la vérification 🔒");
        navigate("/verification");
      } else {
        toast.success("Profil créé ! Bienvenue sur KoraLove 🎉");
        navigate("/decouvrir");
      }
    } catch (e) {
      toast.error("Une erreur est survenue.");
    } finally {
      setSaving(false);
    }
  };

  const Chip = ({ active, onClick, children, testid, error }) => (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`px-5 py-3 rounded-full font-medium text-sm transition-all border ${
        active
          ? "bg-green-600 text-white border-green-600 shadow-[0_4px_14px_rgba(34,197,94,0.35)]"
          : error
          ? "bg-red-50 text-kora-ink border-red-400"
          : "bg-white text-kora-ink border-kora-line hover:border-green-500/40"
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="min-h-screen bg-kora-sand">
      <div className="max-w-md mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-kora-terracotta flex items-center justify-center">
              <Heart size={16} className="text-white" fill="white" />
            </div>
            <span className="font-display font-bold text-xl text-kora-ink">KoraLove</span>
          </div>
          <button
            type="button"
            data-testid="onboarding-back-home"
            onClick={() => navigate("/", { state: { stay: true } })}
            className="flex items-center gap-1.5 text-sm font-semibold text-kora-terracotta hover:underline"
          >
            <ArrowLeft size={16} /> Accueil
          </button>
        </div>
        <h1 className="font-display font-black text-3xl text-kora-ink mt-4">Créez votre profil</h1>
        <p className="text-kora-ink/60 mt-1 mb-6">Quelques infos pour bien vous présenter.</p>

        <div className="space-y-6">
          {/* Photos */}
          <div className={fieldValid.photos ? "" : isErr("photos") ? "p-3 rounded-2xl border-2 border-red-400 bg-red-50" : ""}>
            <label className="block text-sm font-semibold text-kora-ink mb-2">Vos photos <LabelStatus k="photos" /></label>
            <PhotoUploader photos={photos} onChange={onPhotos} />
          </div>

          <div>
            <label className="block text-sm font-semibold text-kora-ink mb-2">Votre prénom <LabelStatus k="name" /></label>
            <input
              data-testid="input-name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Ex. Awa"
              className={inputCls("name")}
            />
            <p className="text-xs text-kora-muted mt-1">C'est ce prénom qui sera affiché sur votre profil.</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-kora-ink mb-2">Je suis <LabelStatus k="gender" /></label>
            <div className="flex gap-3">
              <Chip testid="gender-homme" error={isErr("gender")} active={form.gender === "homme"} onClick={() => set("gender", "homme")}>Un homme</Chip>
              <Chip testid="gender-femme" error={isErr("gender")} active={form.gender === "femme"} onClick={() => set("gender", "femme")}>Une femme</Chip>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-kora-ink mb-2">Je recherche <LabelStatus k="interested_in" /></label>
            <div className="flex gap-3 flex-wrap">
              <Chip testid="interest-femme" error={isErr("interested_in")} active={form.interested_in === "femme"} onClick={() => set("interested_in", "femme")}>Une femme</Chip>
              <Chip testid="interest-homme" error={isErr("interested_in")} active={form.interested_in === "homme"} onClick={() => set("interested_in", "homme")}>Un homme</Chip>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-kora-ink mb-2">Quel est votre but ? <LabelStatus k="goal" /></label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="goal-amicale"
                onClick={() => set("goal", "amicale")}
                className={`p-4 rounded-2xl border text-left transition-all ${form.goal === "amicale" ? "bg-green-50 border-green-500 shadow-[0_4px_14px_rgba(34,197,94,0.18)]" : isErr("goal") ? "bg-red-50 border-red-400" : "bg-white border-kora-line"}`}
              >
                <Users size={22} className="text-[#C77826] mb-2" />
                <p className="font-display font-bold text-kora-ink">Amicale</p>
                <p className="text-xs text-kora-muted">Se faire des amis</p>
              </button>
              <button
                type="button"
                data-testid="goal-mariage"
                onClick={() => set("goal", "mariage")}
                className={`p-4 rounded-2xl border text-left transition-all ${form.goal === "mariage" ? "bg-green-50 border-green-500 shadow-[0_4px_14px_rgba(34,197,94,0.18)]" : isErr("goal") ? "bg-red-50 border-red-400" : "bg-white border-kora-line"}`}
              >
                <Sparkles size={22} className="text-kora-sage mb-2" />
                <p className="font-display font-bold text-kora-ink">Mariage</p>
                <p className="text-xs text-kora-muted">Relation sérieuse</p>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-kora-ink mb-2">Âge <LabelStatus k="age" /></label>
              <input
                data-testid="input-age"
                type="number"
                min="18"
                value={form.age}
                onChange={(e) => set("age", e.target.value)}
                placeholder="25"
                className={inputCls("age")}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-kora-ink mb-2">Pays d'origine <LabelStatus k="city" /></label>
              <select
                data-testid="select-city"
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                className={inputCls("city")}
              >
                <option value="">Choisir…</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-kora-ink mb-2">Pays de résidence <LabelStatus k="residence_country" /></label>
              <select
                data-testid="select-residence"
                value={form.residence_country}
                onChange={(e) => set("residence_country", e.target.value)}
                className={inputCls("residence_country")}
              >
                <option value="">Choisir…</option>
                {RESIDENCE_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-kora-ink mb-2">Langue parlée <LabelStatus k="languages" /></label>
              <select
                data-testid="select-languages"
                value={form.languages}
                onChange={(e) => set("languages", e.target.value)}
                className={inputCls("languages")}
              >
                <option value="">Choisir…</option>
                {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.emoji} {l.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-kora-ink mb-2">Ma silhouette <LabelStatus k="body_type" /></label>
            <div className={`flex flex-wrap gap-2 ${isErr("body_type") ? "p-3 rounded-2xl border-2 border-red-400 bg-red-50" : ""}`} data-testid="bodytype-group">
              {["Ronde", "Mince", "Athlétique", "Normale", "Quelques kilos en trop"].map((b) => (
                <button
                  key={b}
                  type="button"
                  data-testid={`bodytype-${b}`}
                  onClick={() => set("body_type", b)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${form.body_type === b ? "bg-green-600 text-white border-green-600" : "bg-white text-kora-ink border-kora-line hover:border-green-500/40"}`}
                >
                  {b}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-kora-ink mb-2">Taille (cm) <LabelStatus k="height" /></label>
            <input
              data-testid="input-height"
              type="number"
              value={form.height}
              onChange={(e) => set("height", e.target.value)}
              placeholder="175"
              className={inputCls("height")}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-kora-ink mb-2">Niveau d'étude <LabelStatus k="education" /></label>
            <div className={`flex flex-wrap gap-2 ${isErr("education") ? "p-3 rounded-2xl border-2 border-red-400 bg-red-50" : ""}`} data-testid="education-group">
              {["Primaire", "Secondaire", "Supérieure"].map((e) => (
                <button
                  key={e}
                  type="button"
                  data-testid={`education-${e}`}
                  onClick={() => set("education", e)}
                  className={`px-5 py-3 rounded-full text-sm font-medium border transition-all ${form.education === e ? "bg-green-600 text-white border-green-600" : "bg-white text-kora-ink border-kora-line hover:border-green-500/40"}`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          {/* Informations personnelles */}
          <div className="pt-2">
            <h2 className="font-display font-black text-xl text-kora-ink mb-1">Informations personnelles</h2>
            <p className="text-sm text-kora-muted mb-4">Ces infos apparaissent sur votre profil et aident à mieux vous connaître.</p>
            <div className="space-y-4">
              <Section emoji="👨‍👩‍👧" title="Enfants" question="Avez-vous des enfants ?" error={isErr("has_children")} status={<LabelStatus k="has_children" />}>
                <CardRadio
                  group="has-children"
                  options={HAS_CHILDREN}
                  value={form.has_children}
                  onSelect={(v) => set("has_children", v)}
                />
              </Section>

              <Section emoji="💍" title="Situation matrimoniale" question="Quelle est votre situation matrimoniale ?" error={isErr("marital_status")} status={<LabelStatus k="marital_status" />}>
                <CardRadio group="marital-status" options={MARITAL_STATUS} value={form.marital_status} onSelect={(v) => set("marital_status", v)} />
              </Section>

              <Section emoji="🚭" title="Tabac" question="Fumez-vous ?" error={isErr("smoking")} status={<LabelStatus k="smoking" />}>
                <CardRadio group="smoking" options={SMOKING} value={form.smoking} onSelect={(v) => set("smoking", v)} />
              </Section>

              <Section emoji="🍷" title="Alcool" question="Consommez-vous de l'alcool ?" error={isErr("alcohol")} status={<LabelStatus k="alcohol" />}>
                <CardRadio group="alcohol" options={ALCOHOL} value={form.alcohol} onSelect={(v) => set("alcohol", v)} />
              </Section>
            </div>
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-2">
              <label className="block text-sm font-semibold text-kora-ink">Vos centres d'intérêt <LabelStatus k="interests" /></label>
              <span className={`text-xs ${isErr("interests") ? "text-red-500 font-bold" : "text-kora-muted"}`}>{interests.length}/{MAX_INTERESTS} · min. {MIN_INTERESTS}</span>
            </div>
            <div className={`flex flex-wrap gap-2 ${isErr("interests") ? "p-3 rounded-2xl border-2 border-red-400 bg-red-50" : ""}`} data-testid="interests-group">
              {INTERESTS.map((it) => {
                const active = interests.includes(it);
                return (
                  <button
                    key={it}
                    type="button"
                    data-testid={`interest-${it}`}
                    onClick={() => toggleFrom(it, interests, setInterests, MAX_INTERESTS)}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${active ? "bg-green-600 text-white border-green-600" : "bg-white text-kora-ink border-kora-line hover:border-green-500/40"}`}
                  >
                    {it}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-2">
              <label className="block text-sm font-semibold text-kora-ink">Ce que vous recherchez chez un partenaire <LabelStatus k="partner_values" /></label>
              <span className={`text-xs ${isErr("partner_values") ? "text-red-500 font-bold" : "text-kora-muted"}`}>{partnerValues.length}/{MAX_VALUES}</span>
            </div>
            <div className={`flex flex-wrap gap-2 ${isErr("partner_values") ? "p-3 rounded-2xl border-2 border-red-400 bg-red-50" : ""}`} data-testid="values-group">
              {PARTNER_VALUES.map((v) => {
                const active = partnerValues.includes(v);
                return (
                  <button
                    key={v}
                    type="button"
                    data-testid={`value-${v}`}
                    onClick={() => toggleFrom(v, partnerValues, setPartnerValues, MAX_VALUES)}
                    className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${active ? "bg-kora-sage text-white border-kora-sage" : "bg-white text-kora-ink border-kora-line hover:border-kora-sage/40"}`}
                  >
                    {v}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-kora-ink mb-2">
              Présentez-vous en quelques mots : vos passions, votre personnalité et ce que vous recherchez. (30 à 300 caractères) <LabelStatus k="bio" />
            </label>
            <textarea
              data-testid="input-bio"
              value={form.bio}
              onChange={(e) => set("bio", e.target.value)}
              rows={4}
              minLength={30}
              maxLength={300}
              placeholder="Passionné de voyage et de cuisine. J'aime les discussions sincères, les sorties entre amis et je recherche une relation sérieuse basée sur le respect et la confiance."
              className={`${inputCls("bio")} resize-none`}
            />
            <p className={`text-xs mt-1 text-right ${isErr("bio") ? "text-red-500 font-bold" : "text-kora-muted"}`}>{form.bio.length}/300{form.bio.trim().length < 30 ? ` (min. 30)` : ""}</p>
          </div>

          {showErrors && missing.length > 0 && (
            <div data-testid="onboarding-errors" className="bg-red-50 border-2 border-red-500 rounded-2xl p-5 shadow-[0_8px_24px_rgba(239,68,68,0.15)]">
              <p className="font-display font-bold text-red-600 flex items-center gap-2 text-base">
                <AlertCircle size={20} /> Il reste {missing.length} information{missing.length > 1 ? "s" : ""} à compléter :
              </p>
              <ul className="mt-2.5 space-y-1.5 text-sm text-red-600 font-semibold list-disc list-inside">
                {missing.map((k) => <li key={k}>{FIELD_LABELS[k]}</li>)}
              </ul>
              <p className="text-xs text-red-500 mt-3">Les champs concernés sont marqués en rouge dans le formulaire ci-dessus.</p>
            </div>
          )}

          <button
            data-testid="onboarding-submit"
            onClick={submit}
            disabled={saving}
            className="w-full bg-kora-terracotta text-white rounded-full py-4 font-bold shadow-[0_4px_14px_rgba(224,93,54,0.39)] hover:bg-[#C94F2B] transition-colors disabled:opacity-60"
          >
            {saving ? "Enregistrement…" : "Commencer à découvrir"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
